import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import Popup from "reactjs-popup";
import {
  FaPowerOff,
  FaUsers,
  FaUserPlus,
  FaChevronRight,
  FaBars,
  FaTimes,
  FaMoneyBill,
  FaUserCircle,
  FaMedal,
  FaUserFriends,
} from "react-icons/fa";
import { BiCreditCard } from "react-icons/bi";
import { GiTrophyCup } from "react-icons/gi";
import { MdDarkMode, MdLightMode } from "react-icons/md";
import { SiGoogleanalytics } from "react-icons/si";

import wabmIcon from "../props/logo/logo192.png";
import { useAppContext } from "../contexts/AppContextProvider";
import { canViewPartnerRecharges } from "../utils/partnerAccess.js";

const Sidebar = () => {
  const { apiCallLogout, partnerInfo } = useAppContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");

  const partnerTypeConfig = {
    gold: {
      icon: GiTrophyCup,
      color: "text-yellow-500",
      bg: "bg-yellow-500/10",
    },
    silver: {
      icon: FaMedal,
      color: "text-slate-400",
      bg: "bg-slate-400/10",
    },
    bronze: {
      icon: FaMedal,
      color: "text-orange-600",
      bg: "bg-orange-600/10",
    },
    referral: {
      icon: FaUserFriends,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
    },
  };

  const partnerType = partnerInfo?.partnerType
    ? partnerInfo.partnerType.charAt(0).toUpperCase() +
      partnerInfo.partnerType.slice(1)
    : "" || "Unknown";
  const typeStyle = partnerTypeConfig[partnerType.toLowerCase()];
  const PartnerIcon = typeStyle?.icon || FaMedal;

  const navigationItems = [
    {
      to: "/dashboard/all-users",
      icon: FaUsers,
      label: "All Users",
      description: "Manage all users",
    },
    {
      to: "/dashboard/registrations",
      icon: FaUserPlus,
      label: "Registrations",
      description: "New user registrations",
    },
    ...(canViewPartnerRecharges(partnerInfo)
      ? [
          {
            to: "/dashboard/all-recharges",
            icon: FaMoneyBill,
            label: "All Recharges",
            description: "All recharges done by users",
          },
        ]
      : []),
    {
      to: "/dashboard/pricing",
      icon: BiCreditCard,
      label: "Pricing",
      description: "All pricing",
    },
    {
      to: "/dashboard/analytics",
      icon: SiGoogleanalytics,
      label: "Analytics",
      description: "Analytics",
    },
    {
      to: "/dashboard/profile",
      icon: FaUserCircle,
      label: "Profile",
      description: "Partner account details",
    },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    localStorage.setItem("theme", theme);

    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="md:hidden pl-4 pt-4 z-50">
        <button
          onClick={toggleMobileMenu}
          className="p-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-colors duration-200 shadow-lg"
        >
          {isMobileMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed md:static inset-y-0 left-0 z-40 w-80  h-screen bg-backgroundPrimary border-r border-borderColor transform transition-transform duration-300 ease-in-out
          ${
            isMobileMenuOpen
              ? "translate-x-0"
              : "-translate-x-full md:translate-x-0"
          }
        `}
      >
        {/* Logo Section */}
        <div className="flex-shrink-0 flex items-center p-6 border-b border-borderColor mt-0">
          <div className="flex items-center gap-3">
            <img
              src={wabmIcon}
              alt="WaBM Partner"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h2 className="text-lg font-semibold text-textPrimary">WaBM</h2>
              <p className="text-xs text-textSecondary">Partner Dashboard</p>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <nav className="flex-1 flex flex-col gap-1 p-3 overflow-y-auto">
          <div
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 mb-2 text-sm font-medium ${
              typeStyle?.bg || "bg-muted"
            }`}
          >
            <PartnerIcon
              className={`h-4 w-4 ${
                typeStyle?.color || "text-muted-foreground"
              }`}
            />
            <span className={typeStyle?.color || "text-muted-foreground"}>
              {partnerType}
            </span>
          </div>

          <div className="my-2">
            <h3 className="text-xs font-medium text-textSecondary uppercase tracking-wider px-3">
              Navigation
            </h3>
          </div>

          {navigationItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeMobileMenu}
              className={({ isActive }) => `
                group relative flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200
                ${
                  isActive
                    ? "bg-accent text-white"
                    : "text-textPrimary hover:bg-backgroundSecondary"
                }
              `}
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    size={18}
                    className={`flex-shrink-0 ${
                      isActive
                        ? "text-white"
                        : "text-slate-400 group-hover:text-textPrimary ease-in-out duration-300"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={` truncate ${
                        isActive
                          ? "text-white"
                          : "text-textSecondary  group-hover:text-textPrimary ease-in-out duration-300"
                      }`}
                    >
                      {item.label}
                    </div>
                    {/* <div
                      className={`text-xs truncate ${isActive ? "text-emerald-100" : "text-slate-400 "
                        }`}
                    >
                      {item.description}
                    </div> */}
                  </div>
                  {isActive && (
                    <FaChevronRight size={12} className="text-white" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Actions Section */}
        <div className="flex-shrink-0 p-3 py-1.5 border-t border-borderColor fixed bottom-0 w-full">
          <button
            onClick={toggleTheme}
            className="mt-auto  w-full flex items-center gap-2 rounded-lg p-3 transition text-gray-400 hover:text-textPrimary hover:bg-backgroundSecondary"
          >
            {theme === "dark" ? (
              <>
                <MdLightMode
                  size={20}
                  className="text-textSecondary hover:text-textPrimary"
                />
                Light Mode
              </>
            ) : (
              <>
                <MdDarkMode
                  size={20}
                  className="text-textSecondary hover:text-textPrimary"
                />
                Dark Mode
              </>
            )}
          </button>
          <Popup
            trigger={
              <button className="w-full group flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-red-400 hover:text-white hover:bg-red-600 transition-all duration-200">
                <FaPowerOff size={16} className="flex-shrink-0" />
                <span className="flex-1 text-left">Log Out</span>
                <FaChevronRight
                  size={12}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                />
              </button>
            }
            modal
            closeOnDocumentClick
            nested
          >
            {(close) => (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h3 className="modal-title">Confirm Logout</h3>

                  <div className="modal-body">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <FaPowerOff className="text-red-600" size={20} />
                      </div>

                      <p className=" mb-4">
                        Are you sure you want to log out? You'll need to sign in
                        again to access the dashboard.
                      </p>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button onClick={close}>Cancel</button>
                    <button
                      onClick={() => {
                        apiCallLogout();
                        close();
                      }}
                      className="button-cancel flex items-center gap-2"
                    >
                      <FaPowerOff size={14} />
                      Log Out
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Popup>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
